import type { ConversationMessage, ConversationToolPart } from "@health/types";
import { normalizeToolPartType } from "@health/types/ui-tools";
import type { UIMessage } from "ai";

/**
 * Canonical outcome shape for the `show_auth` widget submission.
 *
 * Single source of truth shared by the SDK widget and the server-side handler
 * so they cannot drift on what counts as "did not authenticate." Every dismiss
 * path (cancel button, modal close, backdrop click, browser back, timeout)
 * lands in `kind: "dismissed"` with a distinct `reason`. Server treats every
 * `dismissed` value identically when `auth_mode === "blocking"`.
 */
export type AuthWidgetOutcome =
  | {
      kind: "authenticated";
      method: "signup" | "signin" | "email_capture";
      email?: string;
      firstName?: string;
      lastName?: string;
    }
  | {
      kind: "dismissed";
      reason: "cancel_button" | "modal_close" | "backdrop_click" | "navigation" | "timeout";
    }
  | {
      kind: "error";
      code: string;
      message: string;
    };

const AUTH_METHODS = ["signup", "signin", "email_capture"] as const;
const DISMISS_REASONS = [
  "cancel_button",
  "modal_close",
  "backdrop_click",
  "navigation",
  "timeout",
] as const;

export function parseAuthWidgetOutcome(value: unknown): AuthWidgetOutcome | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (v.kind === "authenticated") {
    const method = v.method;
    if (
      typeof method !== "string" ||
      !AUTH_METHODS.includes(method as (typeof AUTH_METHODS)[number])
    )
      return null;
    return {
      kind: "authenticated",
      method: method as (typeof AUTH_METHODS)[number],
      email: typeof v.email === "string" ? v.email : undefined,
      firstName: typeof v.firstName === "string" ? v.firstName : undefined,
      lastName: typeof v.lastName === "string" ? v.lastName : undefined,
    };
  }
  if (v.kind === "dismissed") {
    const reason = v.reason;
    if (
      typeof reason !== "string" ||
      !DISMISS_REASONS.includes(reason as (typeof DISMISS_REASONS)[number])
    )
      return null;
    return { kind: "dismissed", reason: reason as (typeof DISMISS_REASONS)[number] };
  }
  if (v.kind === "error") {
    if (typeof v.code !== "string" || typeof v.message !== "string") return null;
    return { kind: "error", code: v.code, message: v.message };
  }
  return null;
}

/**
 * Compatibility shim: maps the legacy `{ action: "cancelled" | "signup" | ... }`
 * shape (used by today's AuthWidget) onto the canonical {@link AuthWidgetOutcome}.
 *
 * Time-boxed — remove once AuthWidget and downstream normalizers consume the
 * canonical shape directly. Other widgets (PhotoUpload, ShippingAddress, etc.)
 * still emit the legacy shape; they will migrate in a follow-up PR.
 */
export function legacyAuthOutputToOutcome(legacy: unknown): AuthWidgetOutcome | null {
  if (!legacy || typeof legacy !== "object") return null;
  const v = legacy as Record<string, unknown>;
  const action = v.action;
  if (action === "cancelled") {
    return { kind: "dismissed", reason: "cancel_button" };
  }
  if (action === "signup" || action === "signin" || action === "email_capture") {
    return {
      kind: "authenticated",
      method: action,
      email: typeof v.email === "string" ? v.email : undefined,
      firstName: typeof v.firstName === "string" ? v.firstName : undefined,
      lastName: typeof v.lastName === "string" ? v.lastName : undefined,
    };
  }
  return null;
}

type ToolLikePart = {
  type: string;
  toolCallId: string;
  state?: ConversationToolPart["state"];
  input?: unknown;
  output?: unknown;
  submitted?: boolean;
};

export function toTransportMessages(messages: UIMessage[]): ConversationMessage[] {
  return messages.map((message) => ({
    id: message.id,
    role: message.role,
    parts: (message.parts ?? []).flatMap<ConversationMessage["parts"][number]>((part) => {
      if (part.type === "text") {
        return [{ type: "text", text: part.text }];
      }

      if (!part.type.startsWith("tool-") || !("toolCallId" in part)) {
        return [];
      }

      const toolPart = part as ToolLikePart;

      return [
        {
          type: normalizeToolPartType(toolPart.type) as `tool-${string}`,
          toolCallId: toolPart.toolCallId,
          state: toolPart.state,
          input:
            toolPart.input && typeof toolPart.input === "object"
              ? (toolPart.input as Record<string, unknown>)
              : undefined,
          output: toolPart.output,
          submitted: typeof toolPart.submitted === "boolean" ? toolPart.submitted : undefined,
        } satisfies ConversationToolPart,
      ];
    }),
  }));
}

export function fromTransportMessages(messages: ConversationMessage[]): UIMessage[] {
  return messages.map((message) => ({
    id: message.id,
    role: message.role,
    parts: (message.parts ?? []).map((part) => {
      if (part.type === "text") {
        return part;
      }

      return {
        type: normalizeToolPartType(part.type) as `tool-${string}`,
        toolCallId: part.toolCallId,
        state: part.state,
        input: part.input ?? {},
        output: part.output,
        ...(part.submitted === undefined ? {} : { submitted: part.submitted }),
      };
    }),
  })) as UIMessage[];
}
